import type {
  CurrentAdminRbacPermissionsResponse,
  CurrentAdminRbacV2Grant,
  RbacV2AbilityTuple,
} from '@gozoki/api-types'

/**
 * Comptes de pointage (« tablette ») en régime canonique.
 *
 * Ces comptes n'ont plus aucun bit : ils portent le rôle `pointage-boutique` (`read`/`add`
 * `hr.clocking.clocking`, `read hr.staff.identity`, `read hr.staff.collaborator`) affecté sur LEUR
 * boutique. Leur seul écran est la tablette de pointage, d'où la redirection dédiée à la connexion.
 *
 * Module pur (aucun import React ni CASL) : la règle se teste seule (`tablet-account.test.mjs`) et
 * ne dépend que du contrat `GET /rbac/me/permissions`.
 */

/** Ressource des pointages dans la taxonomie v2. */
export const CLOCKING_RESOURCE = 'hr.clocking.clocking' as const

/** Exigence de l'écran de pointage : créer un pointage. */
export const CLOCKING_ADD_PERMISSION: RbacV2AbilityTuple = ['add', CLOCKING_RESOURCE]

/**
 * Ressources qu'un compte de pointage peut porter — et rien d'autre : les deux lectures RH servent
 * à afficher la liste des collaborateurs de la boutique sur la tablette.
 *
 * Toute autre ressource (dont `dashboard.dashboard.global-indicators`, `stores.store-list.store`,
 * `hr.planning.weekly-planning`) disqualifie le compte : un manager qui pointe aussi garde la
 * navigation normale du back-office et n'est pas enfermé sur la tablette.
 */
const TABLET_RESOURCES = new Set<string>([
  CLOCKING_RESOURCE,
  'hr.staff.identity',
  'hr.staff.collaborator',
])

/** Périmètre boutique d'un ensemble de grants : `all`, ou l'union de leurs boutiques. */
const grantsStoreScope = (grants: CurrentAdminRbacV2Grant[]): 'all' | number[] => {
  // Un grant dont le périmètre n'est pas sérialisé ne restreint rien : on ne déduit jamais une
  // restriction d'une information absente (le serveur, lui, tranche cible par cible).
  if (grants.some((grant) => grant.storeIds === undefined || grant.storeIds === 'all')) return 'all'

  return [...new Set(grants.flatMap((grant) => grant.storeIds as number[]))]
}

const clockingGrants = (
  contract: CurrentAdminRbacPermissionsResponse,
  action?: string,
): CurrentAdminRbacV2Grant[] =>
  contract.grantsV2.filter(
    (grant) =>
      grant.resource === CLOCKING_RESOURCE && (action === undefined || grant.action === action),
  )

/**
 * Boutique sur laquelle rediriger un compte de pointage à la connexion, ou `null` si le compte
 * n'est pas une tablette (ou si son périmètre ne désigne pas une boutique unique : `all` ou
 * plusieurs boutiques ⇒ on laisse la navigation normale choisir le premier écran autorisé).
 *
 * `contract` à `null` (réponse absente, en erreur ou illisible) ⇒ `null` : aucune redirection
 * spéciale, jamais de blocage.
 */
export const resolveTabletStoreId = (
  contract: CurrentAdminRbacPermissionsResponse | null,
): number | null => {
  if (contract === null) return null

  // Un compte tablette pointe : sans `add hr.clocking.clocking`, ce n'en est pas un.
  if (clockingGrants(contract, 'add').length === 0) return null

  // …et ne fait que ça : la moindre permission hors périmètre tablette rend la main au menu.
  if (contract.grantsV2.some((grant) => !TABLET_RESOURCES.has(grant.resource))) return null

  const scope = grantsStoreScope(clockingGrants(contract))
  if (scope === 'all' || scope.length !== 1) return null

  return scope[0]
}

/**
 * Le compte peut-il pointer dans cette boutique ? Sert à afficher « Accès refusé » plutôt que
 * l'erreur brute du serveur (`assertAdminClockingTarget`) quand une tablette ouvre l'URL d'une
 * autre boutique que la sienne.
 *
 * Ne répond `false` que sur une restriction EXPLICITE : contrat illisible, ou aucun grant
 * `add hr.clocking.clocking` (régime hérité, où l'accès se décide encore sur les bits) ⇒ on ne
 * bloque pas, le serveur reste l'autorité et la garde de permission de l'écran a déjà tranché.
 */
export const canClockInStore = (
  contract: CurrentAdminRbacPermissionsResponse | null,
  storeId: number,
): boolean => {
  if (contract === null) return true

  const grants = clockingGrants(contract, 'add')
  if (grants.length === 0) return true

  const scope = grantsStoreScope(grants)
  if (scope === 'all') return true

  return scope.includes(storeId)
}
